/**
 * useLocation — foreground-only geolocation hook.
 *
 * Works on:
 *  - Web (browsers via `navigator.geolocation`)
 *  - iOS / Android native via `@capacitor/geolocation`
 *
 * Strict opt-in per call — never polls in the background, never tracks
 * position changes. The captain taps a button → we ask the OS once → we
 * return one fix → we're done.
 *
 * Returns the fix shape:
 *   { lat, lng, accuracy, place_name? }
 */

import { Geolocation } from "@capacitor/geolocation";
import { isNativeApp } from "@/lib/platform";

const NOMINATIM_BASE = "https://nominatim.openstreetmap.org";

/**
 * Reverse-geocode a lat/lng → human place name via OpenStreetMap Nominatim.
 *
 * Falls back to a coordinate string if Nominatim is unreachable or returns
 * nothing useful. Nominatim's usage policy requires a custom UA — we use
 * `User-Agent: BosunsMate/1.0`. They allow ~1 req/sec which is fine for our
 * use case (single fix per entry save).
 */
async function reverseGeocode(lat, lng) {
    try {
        const url = `${NOMINATIM_BASE}/reverse?format=jsonv2&lat=${lat}&lon=${lng}&zoom=12&addressdetails=1`;
        const res = await fetch(url, {
            headers: { "Accept-Language": navigator.language || "en" },
        });
        if (!res.ok) throw new Error("Nominatim " + res.status);
        const data = await res.json();
        const a = data.address || {};
        // Build a marine-friendly name: prefer water body / harbour, then
        // village/town, then state/region, then country.
        const parts = [
            a.bay || a.harbour || a.water || a.marina || a.suburb,
            a.village || a.town || a.city || a.hamlet,
            a.state_district || a.state || a.region || a.county,
            a.country_code ? a.country_code.toUpperCase() : a.country,
        ].filter(Boolean);
        // Dedupe consecutive identical parts (e.g. "BC" twice).
        const deduped = parts.filter((p, i) => i === 0 || p !== parts[i - 1]);
        if (deduped.length > 0) return deduped.slice(0, 3).join(", ");
        if (data.display_name) return data.display_name.split(",").slice(0, 3).join(",").trim();
    } catch (_e) {
        // Network/Nominatim failure is non-fatal — we keep the raw coords.
    }
    return `${lat.toFixed(4)}°, ${lng.toFixed(4)}°`;
}

/**
 * Request a single GPS fix. Throws on permission denial or hardware failure.
 *
 * @param {object} opts
 * @param {boolean} opts.skipReverseGeocode — set true to skip the Nominatim
 *   call (useful for tests or if you want raw coords)
 */
export async function getCurrentLocation({ skipReverseGeocode = false } = {}) {
    const native = isNativeApp();

    let coords;
    if (native) {
        // Capacitor handles permission prompts automatically on first call.
        const perm = await Geolocation.checkPermissions();
        if (perm.location !== "granted") {
            const req = await Geolocation.requestPermissions();
            if (req.location !== "granted") {
                throw new Error("Location permission denied. You can enable it in Settings → Bosun's Mate → Location.");
            }
        }
        const pos = await Geolocation.getCurrentPosition({
            enableHighAccuracy: true,
            timeout: 12000,
            maximumAge: 30000,
        });
        coords = { lat: pos.coords.latitude, lng: pos.coords.longitude, accuracy: pos.coords.accuracy };
    } else {
        // Web — use the browser API.
        if (!("geolocation" in navigator)) {
            throw new Error("Your browser doesn't support location services.");
        }
        coords = await new Promise((resolve, reject) => {
            navigator.geolocation.getCurrentPosition(
                (p) => resolve({ lat: p.coords.latitude, lng: p.coords.longitude, accuracy: p.coords.accuracy }),
                (err) => {
                    if (err.code === err.PERMISSION_DENIED) {
                        reject(new Error("You declined the location prompt. You can re-enable it in your browser's site settings."));
                    } else if (err.code === err.POSITION_UNAVAILABLE) {
                        reject(new Error("Couldn't get a fix — GPS signal unavailable. Try again with a clearer view of the sky."));
                    } else if (err.code === err.TIMEOUT) {
                        reject(new Error("Location lookup timed out. Try again in a moment."));
                    } else {
                        reject(err);
                    }
                },
                { enableHighAccuracy: true, timeout: 12000, maximumAge: 30000 }
            );
        });
    }

    if (skipReverseGeocode) {
        return { ...coords, place_name: null };
    }

    const place_name = await reverseGeocode(coords.lat, coords.lng);
    return { ...coords, place_name };
}
